import {
  DropdownList,
  type DropdownListGroup,
  type DropdownListOptions,
} from "@/components/ui/dropdown-list/dropdown-list";
import type { IDropdownListItem } from "@/components/ui/dropdown-list/dropdown-list-item";
import {
  Button,
  GroupTextField,
  Icon,
  TextField,
} from "@elaniintc/goes-design-system";
import { useEffect, useMemo, useState } from "react";

import MultiSelect from "@/components/ui/multi-select";
import SwitchField from "../form/components/SwitchField";
import type { NodeExpirationConfig, NodeExtensionConfig } from ".";

export type TimerValues = {
  limit_unit?: "minute" | "hour" | "day" | "month" | "year";
  limit_amount?: number | null;
  days?: ("mon" | "tue" | "wed" | "thu" | "fri")[];
  hasTimer?: boolean;
};

type TimerProps = {
  onChange?: (
    values: TimerValues,
    expiration: null | {
      extensionAmount: number | null;
      extensionUnit: TimerValues["limit_unit"] | undefined;
      leaderApproval: boolean;
    },
  ) => void;
  /** Emite true/false cuando el temporizador está visible/oculto */
  onHasTimerChange?: (hasTimer: boolean) => void;
  initialValues?: {
    expiration: Partial<NodeExpirationConfig>;
    extension: Partial<NodeExtensionConfig> | null;
  };
};

// --- Opciones del (primer) DropdownList: ignorado para retorno (está disabled) ---
const options: DropdownListOptions = [
  {
    groupId: "unico-grupo",
    groupLabels: ["Nombre del nodo", "Paso"],
    icon: {
      type: "far",
      name: "bars",
      size: "xs",
      className: "text-action-quiet",
    },
    items: [
      {
        label: "inicio de formulario",
        detail: "str",
        value: "string",
        icon: {
          type: "far",
          name: "calendar",
        },
      },
    ],
  },
];

const weekDayOptions = [
  { label: "Lunes", value: "mon" },
  { label: "Martes", value: "tue" },
  { label: "Miércoles", value: "wed" },
  { label: "Jueves", value: "thu" },
  { label: "Viernes", value: "fri" },
];

// Lista completa de días hábiles para seleccionar por defecto
const ALL_WEEK_DAYS = weekDayOptions.map((o) => o.value);

// --- Segundo select (sí devuelve valor) ---
const optionsLimit: IDropdownListItem[] = [
  { label: "Horas", value: "hour" },
  { label: "Días", value: "day" },
  { label: "Meses", value: "month" },
];

export const Timer = ({
  onChange,
  onHasTimerChange,
  initialValues,
}: TimerProps) => {
  const defaultItem = options[0].items[0];
  const defaultGroup = options[0];

  const [selectedValue] = useState<IDropdownListItem>(defaultItem);
  const [selectedGroup] = useState<DropdownListGroup | undefined>(defaultGroup);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredGroups, setFilteredGroups] = useState(options);

  useEffect(() => {
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      setFilteredGroups(
        options
          .map((group) => ({
            ...group,
            items: group.items.filter(
              (item) =>
                (item.label as string).toLowerCase().includes(lowerQuery) ||
                item.detail?.toLowerCase().includes(lowerQuery) ||
                item.value.toLowerCase().includes(lowerQuery),
            ),
          }))
          .filter((group) => group.items.length > 0),
      );
    } else {
      setFilteredGroups(options);
    }
  }, [searchQuery]);

  const initialUnitItem = useMemo(() => {
    const expiration = initialValues?.expiration;
    if (!expiration?.limit_unit) return undefined;
    return optionsLimit.find((o) => o.value === expiration.limit_unit);
  }, [initialValues?.expiration]);

  const [selectedValueLimit, setSelectedValueLimit] = useState<
    IDropdownListItem | undefined
  >(initialUnitItem);

  const [limitAmountRaw, setLimitAmountRaw] = useState<string>(
    initialValues?.expiration.limit_amount != null
      ? String(initialValues.expiration.limit_amount)
      : "",
  );

  const [limitExtenstionRaw, setLimitExtensionRaw] = useState<string>(
    initialValues?.extension?.extensionAmount != null
      ? String(initialValues.extension.extensionAmount)
      : "",
  );

  const initialExtensionUnitItem = useMemo(() => {
    const extension = initialValues?.extension;
    if (!extension?.extensionUnit) return undefined;
    return optionsLimit.find((o) => o.value === extension.extensionUnit);
  }, [initialValues?.extension]);

  const [selectedValueLimitExtension, setSelectedValueLimitExtension] =
    useState<IDropdownListItem | undefined>(initialExtensionUnitItem);

  const toggleSelectionLimit = (value: IDropdownListItem) => {
    setSelectedValueLimit(value);
  };
  const [needsLeaderApproval, setNeedsLeaderApproval] = useState(false);

  // ✅ DÍAS HÁBILES SELECCIONADOS POR DEFECTO
  const [selectedDays, setSelectedDays] = useState<string[]>(
    initialValues?.expiration.days ?? ALL_WEEK_DAYS,
  );

  // --- VISIBILIDAD DEL BLOQUE DEL TEMPORIZADOR ---
  const computeInitialShow = () => {
    if (initialValues && Object.keys(initialValues).length > 0) return true;
    return false;
  };
  const [showTimer, setShowTimer] = useState<boolean>(computeInitialShow());
  const [showExtension, setShowExtension] = useState<boolean>(false);

  useEffect(() => {
    const shouldShow =
      initialValues && Object.keys(initialValues).length > 0 ? true : false;
    setShowTimer(shouldShow);
  }, [initialValues]);

  useEffect(() => {
    onHasTimerChange?.(showTimer);
  }, [showTimer, onHasTimerChange]);

  const days = (initialValues?.expiration.days ?? []).join(",");
  useEffect(() => {
    const amount = initialValues?.expiration.limit_amount;
    const unit = initialValues?.expiration.limit_unit;
    const daysArr = initialValues?.expiration.days ?? ALL_WEEK_DAYS; // ✅ default a todos si no vienen del padre

    // cantidad
    setLimitAmountRaw(amount != null ? String(amount) : "");

    // unidad
    const unitItem = unit
      ? optionsLimit.find((o) => o.value === unit)
      : undefined;
    setSelectedValueLimit(unitItem);

    // días
    setSelectedDays(daysArr);
    console.log(initialValues?.extension);
    // Si vienen valores de prórroga, mostrar bloque
    if (initialValues?.extension) {
      setShowExtension(true);
      // cantidad
      setLimitExtensionRaw(
        initialValues.extension.extensionAmount != null
          ? String(initialValues.extension.extensionAmount)
          : "",
      );
      // unidad
      const extUnitItem = initialValues.extension.extensionUnit
        ? optionsLimit.find(
            (o) => o.value === initialValues?.extension?.extensionUnit,
          )
        : undefined;
      setSelectedValueLimitExtension(extUnitItem);
      // aprobación líder
      setNeedsLeaderApproval(initialValues.extension.leaderApproval ?? false);
    } else {
      // No vienen valores de prórroga, ocultar bloque y limpiar campos
      setShowExtension(false);
      setLimitExtensionRaw("");
      setSelectedValueLimitExtension(undefined);
      setNeedsLeaderApproval(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialValues?.expiration.limit_amount,
    initialValues?.expiration.limit_unit,
    days,
  ]);

  useEffect(() => {
    if (!showTimer) {
      onChange?.(
        {
          limit_unit: undefined,
          limit_amount: null,
          days: [],
          hasTimer: false,
        },
        null,
      );
      return;
    }

    const limit_amount =
      limitAmountRaw.trim() === "" ? null : Number(limitAmountRaw);

    const unit =
      (selectedValueLimit?.value as TimerValues["limit_unit"] | undefined) ??
      undefined;

    onChange?.(
      {
        limit_unit: unit,
        limit_amount,
        days: selectedDays as TimerValues["days"],
        hasTimer: true,
      },
      showExtension
        ? {
            extensionAmount:
              limitExtenstionRaw.trim() === ""
                ? null
                : Number(limitExtenstionRaw),
            extensionUnit:
              (selectedValueLimitExtension?.value as
                | TimerValues["limit_unit"]
                | undefined) ?? undefined,
            leaderApproval: needsLeaderApproval,
          }
        : null,
    );
  }, [
    limitAmountRaw,
    selectedValueLimit,
    selectedDays,
    showTimer,
    onChange,
    showExtension,
    limitExtenstionRaw,
    selectedValueLimitExtension,
    needsLeaderApproval,
  ]);

  // --- Agregar / Eliminar ---
  const handleAgregar = () => {
    setShowTimer(true);

    // Si el usuario no tenía días seleccionados (p.ej. después de eliminar), reponer por defecto
    setSelectedDays((prev) => (prev.length ? prev : ALL_WEEK_DAYS)); // ✅

    const limit_amount =
      limitAmountRaw.trim() === "" ? null : Number(limitAmountRaw);
    const unit =
      (selectedValueLimit?.value as TimerValues["limit_unit"] | undefined) ??
      undefined;

    onChange?.(
      {
        limit_unit: unit,
        limit_amount,
        days:
          ((selectedDays.length
            ? selectedDays
            : ALL_WEEK_DAYS) as TimerValues["days"]) ?? [],
        hasTimer: true,
      },
      null,
    );
  };

  const handleDeleteTimer = () => {
    setLimitAmountRaw("");
    setSelectedValueLimit(undefined);
    setSelectedDays([]); // al volver a agregar, se repondrá ALL_WEEK_DAYS
    setSearchQuery("");
    setFilteredGroups(options);
    setShowTimer(false);

    setShowExtension(false);
    setLimitExtensionRaw("");
    setSelectedValueLimitExtension(undefined);
    setNeedsLeaderApproval(false);

    onChange?.(
      {
        limit_unit: undefined,
        limit_amount: null,
        days: [],
        hasTimer: false,
      },
      null,
    );
  };

  const handleDeleteExtension = () => {
    setLimitExtensionRaw("");
    setSelectedValueLimitExtension(undefined);
    setShowExtension(false);
    setNeedsLeaderApproval(false);

    const limit_amount =
      limitAmountRaw.trim() === "" ? null : Number(limitAmountRaw);
    const unit =
      (selectedValueLimit?.value as TimerValues["limit_unit"] | undefined) ??
      undefined;

    onChange?.(
      {
        limit_unit: unit,
        limit_amount,
        days: selectedDays as TimerValues["days"],
        hasTimer: true,
      },
      null,
    );
  };

  const canConfigureExtension =
    selectedValue &&
    limitAmountRaw.trim() !== "" &&
    selectedValueLimit &&
    selectedDays.length > 0;

  return (
    <div className="p-4 flex flex-col gap-8">
      <div>
        {!showTimer && (
          <div>
            <Button variant="tertiary" onClick={handleAgregar}>
              Agregar temporizador
              <Icon type="far" name="plus" />
            </Button>
          </div>
        )}

        {showTimer && (
          <>
            <div>
              <Button variant="tertiary" onClick={handleDeleteTimer}>
                Eliminar temporizador
                <Icon type="far" name="times" />
              </Button>
            </div>

            <div className="py-4 flex cursor-not-allowed">
              <GroupTextField
                label="Comenzar a partir de"
                required
                className="flex-1"
              >
                <DropdownList
                  disabled
                  widthClassName="flex-1"
                  placeholder="Elegir variable..."
                  options={filteredGroups}
                  selectedValue={selectedValue}
                  selectedGroup={selectedGroup}
                  toggleSelection={() => {}}
                  search={{
                    placeholder: "Buscar...",
                    query: searchQuery,
                    onSearch: (q: string) => setSearchQuery(q),
                  }}
                />
              </GroupTextField>
            </div>

            <div className="flex items-end">
              <div className="w-full">
                <TextField
                  type="text"
                  label="Finalizar al transcurrir"
                  required
                  placeholder="Escribe la cantidad"
                  value={limitAmountRaw}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const sanitizedValue = e.target.value.replace(
                      /[^0-9]/g,
                      "",
                    );
                    setLimitAmountRaw(sanitizedValue);
                  }}
                  className="py-[10.5px] rounded-none border-r-0 rounded-l-lg"
                  min={0}
                  maxLength={4}
                />
              </div>
              <div className="mb-1">
                <DropdownList
                  widthClassName="w-32"
                  heightClassName="h-35"
                  placeholder="Unidad"
                  group="right"
                  options={optionsLimit}
                  selectedValue={selectedValueLimit}
                  toggleSelection={toggleSelectionLimit}
                />
              </div>
            </div>

            <div className="mt-4">
              <MultiSelect
                label="Días hábiles"
                name="dias-aplicacion"
                className="w-full"
                placeholder="Selecciona los días"
                options={weekDayOptions}
                selectedItems={selectedDays}
                onChange={setSelectedDays}
                required
                disabled={false}
                isLoading={false}
                emptyStateMessage="No hay días disponibles"
                triggerIcon={<Icon type="far" name="calendar" />}
              />
            </div>
          </>
        )}
      </div>

      {!showExtension && (
        <div>
          <Button
            variant="tertiary"
            onClick={() => {
              setShowExtension(true);
            }}
            disabled={!canConfigureExtension}
          >
            Configurar prórroga
            <Icon type="far" name="plus" />
          </Button>
        </div>
      )}
      {showExtension && (
        <div className="flex flex-col gap-4">
          <div>
            <Button variant="tertiary" onClick={handleDeleteExtension}>
              Eliminar prórroga
              <Icon type="far" name="times" />
            </Button>
          </div>
          <div className="flex items-end">
            <div className="w-full">
              <TextField
                type="text"
                label="Añadir prórroga de"
                required
                placeholder="Escribe la cantidad"
                value={limitExtenstionRaw}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  // Solo permitir números y eliminar cualquier otro carácter
                  const sanitizedValue = e.target.value.replace(/[^0-9]/g, "");
                  setLimitExtensionRaw(sanitizedValue);
                }}
                className="py-[10.5px] rounded-none border-r-0 rounded-l-lg"
              />
            </div>
            <div className="mb-1">
              <DropdownList
                widthClassName="w-32"
                heightClassName="h-35"
                placeholder="Unidad"
                group="right"
                options={optionsLimit}
                selectedValue={selectedValueLimitExtension}
                toggleSelection={setSelectedValueLimitExtension}
              />
            </div>
          </div>
          <SwitchField
            label="¿Requiere aprobación de líder?"
            checked={needsLeaderApproval}
            onChange={() => {
              setNeedsLeaderApproval((prev) => !prev);
            }}
          />
        </div>
      )}
    </div>
  );
};
